import React, { useEffect, useState } from "react";
import styles from "./DocumentsTab.module.css";
import DocumentPreviewModal from "../preview/DocumentPreviewModal";
import DeleteStandardPopUp from "../modals/DeleteStandardPopUp";

import viewIcon from "../../../assets/images/show.svg";
import downloadIcon from "../../../assets/images/download.svg";
import moreIcon from "../../../assets/images/more.svg";
import uploadIcon from "../../../assets/images/upload-icon.svg";
// import progressRgGif from "../../../assets/images/progress_rg.gif";
import InlineSpinner from "../../Common/InlineSpinner/InlineSpinner";

import { useUserCapabilities } from "../../../auth.capabilities.js";
import useFilesApi from "../../../api/filesApi";

function downloadBlob(blob, name) {
  if (!blob) return;
  const url = window.URL.createObjectURL(blob);
  const link = document.createElement("a");
  link.href = url;
  link.download = name || "download";
  document.body.appendChild(link);
  link.click();
  document.body.removeChild(link);
  window.setTimeout(() => {
    window.URL.revokeObjectURL(url);
  }, 0);
}

const inferContentType = (fileName) => {
  const ext = String(fileName || "")
    .split(".")
    .pop()
    .toLowerCase();
  if (ext === "pdf") return "application/pdf";
  if (ext === "mp4") return "video/mp4";
  if (ext === "webm") return "video/webm";
  if (ext === "ogg") return "video/ogg";
  if (ext === "docx")
    return "application/vnd.openxmlformats-officedocument.wordprocessingml.document";
  if (ext === "doc") return "application/msword";
  if (ext === "xlsx")
    return "application/vnd.openxmlformats-officedocument.spreadsheetml.sheet";
  if (ext === "xls") return "application/vnd.ms-excel";
  if (ext === "pptx")
    return "application/vnd.openxmlformats-officedocument.presentationml.presentation";
  if (ext === "ppt") return "application/vnd.ms-powerpoint";
  if (ext === "txt") return "text/plain";
  if (ext === "csv") return "text/csv";
  if (ext === "html" || ext === "htm") return "text/html";
  return "";
};

const ensureBlobType = (blob, contentType, fileName) => {
  if (!blob) return null;
  const inferred = contentType || inferContentType(fileName);
  if (!inferred) return { blob, contentType: contentType || "" };
  if (blob.type === inferred) return { blob, contentType: inferred };
  return { blob: new Blob([blob], { type: inferred }), contentType: inferred };
};

const pickPreviewFileName = (...values) => {
  const names = values.filter(
    (v) => typeof v === "string" && v.trim().length > 0,
  );
  const withExtension = names.find((name) =>
    /\.[a-z0-9]{2,5}$/i.test(name.trim()),
  );
  return withExtension || names[0] || "download";
};

const normalizeFile = (file) => {
  const name =
    file?.name || file?.fileName || file?.title || file?.documentName || "File";

  const url =
    file?.url || file?.fileUrl || file?.link || file?.downloadUrl || "";

  const fileId =
    file?.id ||
    file?.fileId ||
    file?.documentId ||
    file?.referenceId ||
    file?.attachmentId ||
    file?.file?.id;

  const restricted =
    Boolean(file?.restricted) ||
    Boolean(file?.isRestricted) ||
    Boolean(file?.private);

  return { name, url, restricted, fileId, raw: file };
};

const getApiErrorMessage = (error, fallback) =>
  error?.response?.data?.message ||
  error?.response?.data?.error ||
  error?.message ||
  fallback;

const getFileActionErrorMessage = (error, action) => {
  const status = Number(error?.response?.status ?? error?.status ?? 0);
  const title = String(error?.response?.data?.title ?? "").trim().toLowerCase();

  if (status === 404 || title === "not found") {
    return "This file could not be found. It may have been removed or is no longer available.";
  }

  if (status === 403) {
    return `You do not have permission to ${action} this file.`;
  }

  return getApiErrorMessage(
    error,
    action === "preview"
      ? "Could not preview this file."
      : "Could not download this file.",
  );
};

const filterHiddenFiles = (files, hiddenFileIds) =>
  files.filter(
    (file) => !file?.fileId || !hiddenFileIds.includes(String(file.fileId)),
  );

const RequestAccessButton = ({ label, onClick }) => {
  return (
    <button
      type="button"
      className={styles.restrictBtn}
      onClick={onClick}
      aria-label={label}
    >
      <span>{label}</span>
      <svg
        xmlns="http://www.w3.org/2000/svg"
        width="24"
        height="24"
        viewBox="0 0 24 24"
        aria-hidden="true"
      >
        <path
          fill="none"
          stroke="#fff"
          strokeLinecap="round"
          strokeLinejoin="round"
          strokeWidth="1.5"
          d="M14.76 12H6.832m0 0c0-.275-.057-.55-.17-.808L4.285 5.814c-.76-1.72 1.058-3.442 2.734-2.591L20.8 10.217c1.46.74 1.46 2.826 0 3.566L7.02 20.777c-1.677.851-3.495-.872-2.735-2.591l2.375-5.378A2 2 0 0 0 6.83 12"
        />
      </svg>
    </button>
  );
};

export default function DocumentsTab({
  standardId,
  openPopup,
  onRequestAccess,
  isParentPopUp = false,
  documents = [],
  references = [],
  filesLoading = false,
  filesError = "",
}) {
  const [isModalOpen, setIsModalOpen] = useState(false);
  const [previewFile, setPreviewFile] = useState(null);
  const [openMenuKey, setOpenMenuKey] = useState("");
  const [deleteTarget, setDeleteTarget] = useState(null);
  const [deleteLoading, setDeleteLoading] = useState(false);
  const [deleteError, setDeleteError] = useState("");
  const [actionError, setActionError] = useState("");
  const [actionInfo, setActionInfo] = useState("");
  const [hiddenFileIds, setHiddenFileIds] = useState([]);
  const caps = useUserCapabilities() || {};
  const hasAdminRights = caps.hasAdminRights === true;
  const showUploadButton = hasAdminRights === true;
  const [downloading, setDownloading] = useState(false);
  const [downloadProgress, setDownloadProgress] = useState(0);
  const [type, setType] = useState(0);

  const { downloadFileBlob, deleteStandardFile, archiveStandardFile } = useFilesApi();

  useEffect(() => {
    setOpenMenuKey("");
    setDeleteTarget(null);
    setDeleteLoading(false);
    setDeleteError("");
    setActionError("");
    setActionInfo("");
    setHiddenFileIds([]);
  }, [standardId]);

  const normalizedDocuments = Array.isArray(documents)
    ? documents.map(normalizeFile)
    : [];
  const normalizedReferences = Array.isArray(references)
    ? references.map(normalizeFile)
    : [];
  const visibleDocuments = filterHiddenFiles(
    normalizedDocuments,
    hiddenFileIds,
  );
  const visibleReferences = filterHiddenFiles(
    normalizedReferences,
    hiddenFileIds,
  );

  const docsEmpty = !filesLoading && visibleDocuments.length === 0;
  const docsAccessible = visibleDocuments.filter((d) => !d.restricted);
  const refsAccessible = visibleReferences.filter((r) => !r.restricted);

  const docsAllRestricted =
    !hasAdminRights &&
    visibleDocuments.length > 0 &&
    docsAccessible.length === 0;
  const refsAllRestricted =
    !hasAdminRights &&
    visibleReferences.length > 0 &&
    refsAccessible.length === 0;

  const getMenuKey = (item) =>
    String(
      item?.fileId ??
        item?.raw?.id ??
        item?.raw?.documentId ??
        item?.name ??
        "",
    );

  const handleRequestAccess = (kind) => {
    if (typeof onRequestAccess === "function") {
      onRequestAccess({ kind, standardId });
      return;
    }
    window.alert("Request access flow not wired yet.");
  };

  const handleArchiveClick = (item) => {
    setOpenMenuKey("");
    setActionError("");
    setActionInfo("");

    const fileId = item?.fileId;
    if (!fileId) {
      setActionError("Archive is only available for saved attachments.");
      return;
    }

    const label = item?.name || "this attachment";
    const confirmed = window.confirm(`Archive ${label}?`);
    if (!confirmed) return;

    archiveStandardFile(fileId)
      .then(() => {
        setHiddenFileIds((current) => {
          const nextFileId = String(fileId);
          return current.includes(nextFileId) ? current : [...current, nextFileId];
        });
        setActionInfo("Attachment archived.");
      })
      .catch((error) => {
        setActionError(getApiErrorMessage(error, "Failed to archive attachment."));
      });
  };

  const handleDeleteClick = (item) => {
    setOpenMenuKey("");
    setDeleteError("");
    setActionError("");
    setActionInfo("");
    setDeleteTarget(item);
  };

  const handleDeleteConfirm = async () => {
    if (deleteLoading) return;

    const fileId = deleteTarget?.fileId;
    if (!fileId) {
      setDeleteError("Delete is only available for saved attachments.");
      return;
    }

    setDeleteLoading(true);
    setDeleteError("");
    setActionError("");
    setActionInfo("");

    try {
      await deleteStandardFile(fileId);
      setHiddenFileIds((current) => {
        const nextFileId = String(fileId);
        return current.includes(nextFileId)
          ? current
          : [...current, nextFileId];
      });
      setDeleteTarget(null);
      setActionInfo("Attachment deleted.");
    } catch (error) {
      setDeleteError(getApiErrorMessage(error, "Failed to delete attachment."));
    } finally {
      setDeleteLoading(false);
    }
  };

  const handlePreview = async (ref) => {
    setActionError("");
    setActionInfo("");

    try {
      setDownloading(true);
      setDownloadProgress(0);
      setType("Loading file");

      if (!ref.fileId) return;
      if (ref.restricted && !hasAdminRights) return;

      const res = await downloadFileBlob(ref.fileId, (percent) => {
        setDownloadProgress(percent);
      });

      if (!res?.blob) {
        setActionError("Could not preview this file right now.");
        return;
      }

      const fileName = pickPreviewFileName(
        res.name && res.name !== "download" ? res.name : "",
        ref.raw?.name,
        ref.raw?.fileName,
        ref.raw?.documentName,
        ref.name,
      );

      const adjusted = ensureBlobType(res.blob, res.contentType, fileName);
      if (!adjusted?.blob) return;

      const blobUrl = window.URL.createObjectURL(adjusted.blob);
      const previewFileObj = {
        name: fileName,
        url: ref.url || "",
        blobUrl,
        contentType:
          adjusted.contentType ||
          res.contentType ||
          ref.raw?.contentType ||
          ref.raw?.mimeType ||
          "",
      };
      // console.log(
      //   "DocumentsTab: Setting preview file (reference):",
      //   previewFileObj,
      // );
      setPreviewFile(previewFileObj);
      setIsModalOpen(true);
    } catch (error) {
      setPreviewFile(null);
      setIsModalOpen(false);
      setActionError(getFileActionErrorMessage(error, "preview"));
    } finally {
      setDownloading(false);
      setDownloadProgress(0);
    }
  };
  const handleDownload = async (att) => {
    setActionError("");
    setActionInfo("");

    try {
      setDownloading(true);
      setDownloadProgress(0);
      setType("Downloading");
      if (!att.fileId) return;
      if (att.restricted && !hasAdminRights) return;
      const res = await downloadFileBlob(att.fileId, (percent) => {
        setDownloadProgress(percent);
      });
      if (!res?.blob) {
        setActionError("Could not download this file right now.");
        return;
      }

      const fileName = pickPreviewFileName(
        res.name && res.name !== "download" ? res.name : "",
        att.raw?.name,
        att.raw?.fileName,
        att.raw?.documentName,
        att.name,
      );
      downloadBlob(res.blob, fileName);
    } catch (error) {
      setActionError(getFileActionErrorMessage(error, "download"));
    } finally {
      setDownloading(false);
      setDownloadProgress(0);
    }
  };

  return (
    <>
      <section
        className={
          !isParentPopUp
            ? styles["doc-widget-panel"]
            : styles["doc-widget-panel-popup "]
        }
        aria-labelledby="docWidget-title"
      >
        <div className={styles["doc-widget-section"]}>
          {filesLoading ? (
            <div className={styles["doc-widget-loading"]}>
              <InlineSpinner />
            </div>
          ) : null}
          {filesError ? (
            <p className={styles.messageError} role="alert">
              {filesError}
            </p>
          ) : null}
          {actionError ? (
            <p className={styles.messageError} role="alert">
              {actionError}
            </p>
          ) : null}
          {actionInfo ? (
            <p className={styles.messageInfo} role="status">
              {actionInfo}
            </p>
          ) : null}
          {!isParentPopUp && (
            <span className={styles["doc-widget-title"]}>
              <h5 id="docWidget-title">Documents</h5>
            </span>
          )}

          {docsEmpty ? <p>No Documents available.</p> : null}

          {docsAllRestricted ? (
            <ul className={styles.documentList}>
              <li>
                <RequestAccessButton
                  label="Request Access to This Document"
                  onClick={() => handleRequestAccess("documents")}
                />
              </li>
            </ul>
          ) : null}

          {!docsAllRestricted && visibleDocuments.length > 0 ? (
            <ul className={styles["doc-widget-list"]}>
              {visibleDocuments.map((att) => (
                <li
                  key={att.raw?.id || att.name}
                  className={styles["doc-widget-item"]}
                >
                  <div className={styles["doc-widget-info"]}>
                    <p className={styles["doc-widget-file-name"]}>
                      Read Standard
                    </p>
                  </div>

                  <div className={styles["doc-widget-actions"]}>
                    <button
                      type="button"
                      className={styles["doc-widget-action-btn"]}
                      disabled={
                        !att.fileId || (att.restricted && !hasAdminRights)
                      }
                      onClick={async () => {
                        handlePreview(att);
                      }}
                    >
                      <img src={viewIcon} alt="Preview document" />
                    </button>

                    <button
                      type="button"
                      className={styles["doc-widget-action-btn"]}
                      disabled={
                        !att.fileId || (att.restricted && !hasAdminRights)
                      }
                      onClick={async () => {
                        handleDownload(att);
                      }}
                      title={
                        att.restricted && !hasAdminRights
                          ? "Request access to download"
                          : "Download"
                      }
                    >
                      <img src={downloadIcon} alt="Download document" />
                    </button>

                    {hasAdminRights ? (
                      <div className={styles.actionMenuWrap}>
                        <button
                          type="button"
                          className={styles["doc-widget-action-btn"]}
                          aria-haspopup="menu"
                          aria-expanded={openMenuKey === getMenuKey(att)}
                          onClick={() =>
                            setOpenMenuKey((current) =>
                              current === getMenuKey(att)
                                ? ""
                                : getMenuKey(att),
                            )
                          }
                        >
                          <img src={moreIcon} alt="More actions" />
                        </button>
                        {openMenuKey === getMenuKey(att) ? (
                          <div className={styles.actionMenu} role="menu">
                            <button
                              type="button"
                              className={styles.actionMenuItem}
                              role="menuitem"
                              onClick={() => handleArchiveClick(att)}
                            >
                              Archive
                            </button>
                            <button
                              type="button"
                              className={styles.actionMenuItem}
                              role="menuitem"
                              onClick={() => handleDeleteClick(att)}
                            >
                              Delete
                            </button>
                          </div>
                        ) : null}
                      </div>
                    ) : null}
                  </div>
                </li>
              ))}
            </ul>
          ) : null}

          {visibleReferences.length > 0 || refsAllRestricted ? (
            <>
              {!isParentPopUp && (
                <span className={styles["doc-widget-title"]}>
                  <h5>References</h5>
                </span>
              )}

              {refsAllRestricted ? (
                <ul className={styles.documentList}>
                  <li>
                    <RequestAccessButton
                      label="Request Access to This Document"
                      onClick={() => handleRequestAccess("references")}
                    />
                  </li>
                </ul>
              ) : null}

              {!refsAllRestricted ? (
                <ul className={styles["doc-widget-list"]}>
                  {visibleReferences.map((ref) => (
                    <li
                      key={ref.raw?.id || ref.name}
                      className={styles["doc-widget-item"]}
                    >
                      <div className={styles["doc-widget-info"]}>
                        <p className={styles["doc-widget-file-name"]}>
                          Read Standard
                        </p>
                      </div>

                      <div className={styles["doc-widget-actions"]}>
                        <button
                          type="button"
                          className={styles["doc-widget-action-btn"]}
                          disabled={
                            !ref.fileId || (ref.restricted && !hasAdminRights)
                          }
                          onClick={() => {
                            handlePreview(ref);
                          }}
                          title={
                            ref.restricted && !hasAdminRights
                              ? "Request access to preview"
                              : "Preview"
                          }
                        >
                          <img src={viewIcon} alt="Preview reference" />
                        </button>

                        <button
                          type="button"
                          className={styles["doc-widget-action-btn"]}
                          disabled={
                            !ref.fileId || (ref.restricted && !hasAdminRights)
                          }
                          onClick={async () => {
                            handleDownload(ref);
                          }}
                          title={
                            ref.restricted && !hasAdminRights
                              ? "Request access to download"
                              : "Download"
                          }
                        >
                          <img src={downloadIcon} alt="Download reference" />
                        </button>
                      </div>
                    </li>
                  ))}
                </ul>
              ) : null}
            </>
          ) : null}

          {showUploadButton ? (
            <button
              type="button"
              className={styles["doc-widget-upload-btn"]}
              onClick={openPopup}
            >
              <img src={uploadIcon} alt="" aria-hidden="true" />
              <span>Upload Attachment</span>
            </button>
          ) : null}
        </div>
      </section>
      {/* ✅ DOWNLOAD PROGRESS OVERLAY */}
      {downloading && (
        <div className={styles.downloadOverlay}>
          <div className={styles.progressBox}>
            <p>
              {type} {downloadProgress}%
            </p>
            <div className={styles.progressBar}>
              <div
                className={styles.progressFill}
                style={{ width: `${downloadProgress}%` }}
              />
            </div>
          </div>
        </div>
      )}

      <DocumentPreviewModal
        isOpen={isModalOpen}
        file={previewFile}
        onClose={() => {
          if (previewFile?.blobUrl)
            window.URL.revokeObjectURL(previewFile.blobUrl);
          setIsModalOpen(false);
          setPreviewFile(null);
        }}
      />
      <DeleteStandardPopUp
        isOpen={Boolean(deleteTarget)}
        title="Delete Attachment?"
        message="Are You Sure You Want To Delete This Attachment?"
        detail="By Selecting Delete This Attachment Will Be Deleted"
        busy={deleteLoading}
        errorMessage={deleteError}
        onConfirm={handleDeleteConfirm}
        onCancel={() => {
          if (deleteLoading) return;
          setDeleteError("");
          setDeleteTarget(null);
        }}
      />
    </>
  );
}

